import { useRuntimeVariables } from 'app-kit'
import { useGeckoAuth } from 'features/auth'
import { useActiveOrgSite } from 'features/orgAndSiteSourceOfTruth/useActiveOrgSite'
import { useCurrentUserScopes } from 'features/permissions/hooks/useCurrentUserScopes'
import { useRolodexPermissionsUserMetadata } from 'features/permissions/hooks/useRolodexPermissionsUserMetadata'
import { useRolodexV3RolesAtScope } from 'features/permissions/hooks/useRolodexV3RolesAtScope'
import type {
  AccessPreview,
  GeckoPermsContextType,
} from 'features/permissions/types'
import { setAccessPreviewMutationGuard } from 'features/permissions/utils/accessPreviewMutationGuard'
import {
  clearAccessPreviewFromStorage,
  readAccessPreviewFromStorage,
  writeAccessPreviewToStorage,
} from 'features/permissions/utils/accessPreviewStorage'
import { computeScopedPermissions } from 'features/permissions/utils/computeScopedPermissions'
import { doesHaveRequiredPermissionsBase } from 'features/permissions/utils/doesHaveRequiredPermissionsBase'
import { NO_V3_ROLES } from 'features/permissions/utils/fetchRolodexV3RolesAtScope'
import { hasGeckoInternalRole } from 'features/permissions/utils/hasGeckoInternalRole'
import {
  activeScopeFor,
  type RolodexV3UserScope,
} from 'features/permissions/utils/policyScopeEvaluation'
import React, {
  createContext,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react'
import type { RolodexPermissionsValues } from 'types'

const NO_SCOPES: readonly RolodexV3UserScope[] = []

export const GeckoPermsContext = createContext<
  GeckoPermsContextType | undefined
>(undefined)

export const GeckoPermissionsProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const { ROLODEX_URL } = useRuntimeVariables()
  const { authorization, authorizedAxios } = useGeckoAuth()

  const { data: currentUserMetadata, mutate: refreshCurrentUser } =
    useRolodexPermissionsUserMetadata(
      authorizedAxios,
      authorization,
      ROLODEX_URL
    )
  const { organizationId, siteId, isResolved } = useActiveOrgSite()
  const activeScope = useMemo(
    () => (isResolved ? activeScopeFor(organizationId, siteId) : undefined),
    [isResolved, organizationId, siteId]
  )
  const { data: v3Roles, error: v3RolesError } = useRolodexV3RolesAtScope(
    authorizedAxios,
    ROLODEX_URL,
    currentUserMetadata?.id,
    authorization,
    activeScope
  )
  const { scopes: accessScopes, error: accessScopesError } =
    useCurrentUserScopes()

  const [showPermissionGates, setShowPermissionGates] = useState<boolean>(false)
  const [storedAccessPreview, setStoredAccessPreview] =
    useState<AccessPreview | null>(() => readAccessPreviewFromStorage())

  const v2RoleNames = useMemo(
    () => currentUserMetadata?.roles.map((role) => role.name),
    [currentUserMetadata]
  )
  const canUseAccessPreview = hasGeckoInternalRole(v2RoleNames ?? [])
  const accessPreview = canUseAccessPreview ? storedAccessPreview : null

  useLayoutEffect(() => {
    setAccessPreviewMutationGuard(Boolean(accessPreview))
    return () => setAccessPreviewMutationGuard(false)
  }, [accessPreview])

  useEffect(() => {
    if (currentUserMetadata && storedAccessPreview && !canUseAccessPreview) {
      clearAccessPreviewFromStorage()
      setStoredAccessPreview(null)
    }
  }, [currentUserMetadata, storedAccessPreview, canUseAccessPreview])

  const startAccessPreview = useCallback(
    (preview: AccessPreview) => {
      if (!canUseAccessPreview) return
      writeAccessPreviewToStorage(preview)
      setStoredAccessPreview(preview)
    },
    [canUseAccessPreview]
  )

  const clearAccessPreview = useCallback(() => {
    clearAccessPreviewFromStorage()
    setStoredAccessPreview(null)
  }, [])

  const permissions = useMemo(
    () =>
      computeScopedPermissions(
        {
          accessPreview,
          v2RoleNames,
          v3Roles: v3RolesError ? NO_V3_ROLES : v3Roles,
          accessScopes: accessScopesError ? NO_SCOPES : accessScopes,
        },
        activeScope
      ),
    [
      accessPreview,
      v2RoleNames,
      v3Roles,
      v3RolesError,
      accessScopes,
      accessScopesError,
      activeScope,
    ]
  )

  const hasPermissions = useCallback(
    (toCheck: RolodexPermissionsValues[]) =>
      doesHaveRequiredPermissionsBase(permissions, toCheck),
    [permissions]
  )

  const value = useMemo(
    () => ({
      user: currentUserMetadata,
      refreshCurrentUser,
      permissions,
      hasPermissions,
      showPermissionGates,
      setShowPermissionGates,
      accessPreview,
      canUseAccessPreview,
      startAccessPreview,
      clearAccessPreview,
    }),
    [
      currentUserMetadata,
      refreshCurrentUser,
      permissions,
      hasPermissions,
      showPermissionGates,
      accessPreview,
      canUseAccessPreview,
      startAccessPreview,
      clearAccessPreview,
    ]
  )

  return (
    <GeckoPermsContext.Provider value={value}>
      {children}
    </GeckoPermsContext.Provider>
  )
}
